// Library for Events that are strongly typed
// see https://github.com/vitaly-t/sub-events
import { SubEvent } from 'sub-events';
import { LogicEngine } from '../LogicEngine/LogicEngine';
import { DataDictionary } from '../utils/DataDictionary';
import { DataQuery } from '../utils/DataQuery';
import { DataRecord } from '../utils/DataRecord';

import { DataServices } from '../utils/DataServices';
import { Participant } from '../Participant/Participant';

import { AuditLog } from '../Participant/AuditLog';

/** Wrapper class for locking and unlocking records */
export class LockManager
{

    constructor(public logicRef : LogicEngine)
    {
    }

    async lockRecord(table_name : string, record_id : string, lock_reason : string = "Locked by Data Monitor", force_lock : boolean = false) : Promise<LockManager.LockResult>
    {
        // Check to see if the account that is active has permission
        if (!this.logicRef.fnHasGroup('DataMonitor')) {
            console.log(`LockManager LockRecord: ${table_name} id: ${record_id} is not a data monitor`);
            return LockManager.LockResult.NotDataMonitor;
        }

        // Load the record
        let record = await DataQuery.instance().executeQueryById(table_name, record_id);
        if (!record)
        {
            console.log(`LockManager LockRecord: ${table_name} id: ${record_id} is not found`);
            return LockManager.LockResult.RecordNotFound;
        }

        if (record.data.lock_by && record.data.lock_by.length > 0)
        {
            if (!force_lock)
            {
                console.log(`LockManager LockRecord: ${table_name} id: ${record_id} is already Locked`);
                return LockManager.LockResult.AlreadyLocked;
            }
        }

        //  Set the lock parameters
        record.data.lock_dt = new Date();
        record.data.lock_by = LogicEngine.currentUser;
        record.data.lock_status = true;
        await record.saveIfNeeded(true);

        //  Create the audit record
        /** Create the audit record */
        const auditText = `Table '${table_name}' locked`;
        const audit = new AuditLog(record.data.participant_id, auditText);
        audit.setTableName(table_name, record.data.id, "lock_dt");
        audit.setEventType(AuditLog.EventTypes.RecordLock);
        audit.setChangeValues("", new Date().toISOString());

        if (record.data.task_id && record.data.task_id.length)
            audit.setReferenceTaskId(record.data.task_id);

        await audit.save();

        //  Create the lock history record
        // Save the task record
        let log : any = {
            participant_id : record.data.participant_id,
            event_dt : new Date(),
            event_dt_local : new Date().toLocaleString(),
            timezone_offset : new Date().getTimezoneOffset(),
            lock_type : 1,
            lock_table : table_name,
            lock_by : LogicEngine.currentUser,
            reason: lock_reason,
        };
        const dataDictionary = await DataServices.instance().getDataDictionary();
        const logRecord : DataRecord = dataDictionary.getRecordFromObject("LockLog", log);
        logRecord.validateId();
        console.log("LockManager Saving", logRecord.data);
        await logRecord.saveIfNeeded(true);

        // If it is a task based record, set the task parent lock status
        if (record.data.task_id && record.data.task_id.length && table_name !== 'Tasks')
        {
            await this.lockRecord("Tasks", record.data.task_id, lock_reason, true);
        }

        console.log(`LockManager LockRecord: ${table_name} id: ${record_id} is now Locked`);
        return LockManager.LockResult.Locked;
    }

    async unlockRecord(table_name : string, record_id : string, unlock_reason : string = "Unlocked by Data Monitor", force_unlock : boolean = false) : Promise<LockManager.LockResult>
    {
        // Load the record
        let record = await DataQuery.instance().executeQueryById(table_name, record_id);
        if (!record)
        {
            console.log(`LockManager Unlock Record: ${table_name} id: ${record_id} is not found`);
            return LockManager.LockResult.RecordNotFound;
        }

        if (!record.data.lock_by || record.data.lock_by.length == 0)
        {
            if (!force_unlock)
            {
                console.log(`LockManager Unlock Record: ${table_name} id: ${record_id} is already Unlocked`);
                return LockManager.LockResult.AlreadyUnlocked;
            }
        }

        let oldValue = new Date(record.data.lock_dt);
        //  Set the lock parameters
        record.data.lock_dt = null;
        record.data.lock_by = '';
        record.data.lock_status = false;
        await record.saveIfNeeded(true);

        //  Create the audit record
        /** Create the audit record */
        const auditText = `Table '${table_name}' unlocked`;
        const audit = new AuditLog(record.data.participant_id, auditText);
        audit.setTableName(table_name, record.data.id, "lock_dt");
        audit.setEventType(AuditLog.EventTypes.RecordLock);
        audit.setChangeValues(oldValue.toISOString(), "");

        if (record.data.task_id && record.data.task_id.length)
            audit.setReferenceTaskId(record.data.task_id);

        await audit.save();

        //  Create the lock history record
        // Save the task record
        let log : any = {
            participant_id : record.data.participant_id,
            event_dt : new Date(),
            event_dt_local : new Date().toLocaleString(),
            timezone_offset : new Date().getTimezoneOffset(),
            lock_type : 0,
            lock_table : table_name,
            lock_by : LogicEngine.currentUser,
            reason: unlock_reason,
        };
        const dataDictionary = await DataServices.instance().getDataDictionary();
        const logRecord : DataRecord = dataDictionary.getRecordFromObject("LockLog", log);
        logRecord.validateId();
        console.log("Saving", logRecord.data);
        await logRecord.saveIfNeeded(true);

        // If it is a task based record, set the task parent lock status
        if (record.data.task_id && record.data.task_id.length && table_name !== 'Tasks')
        {
            await this.unlockRecord("Tasks", record.data.task_id, unlock_reason, true);
        }

        console.log(`LockManager UnlockReocrd: ${table_name} id: ${record_id} is now unlocked`);
        return LockManager.LockResult.Unlocked;
    }

}

export namespace LockManager
{
    export enum LockResult {
        AlreadyLocked = 1,
        Locked  = 2,
        Unlocked = 3,
        FailedToLock = 4,
        NotDataMonitor = 5,
        RecordNotFound = 6,
        AlreadyUnlocked = 7,
    };
}
